import {
  InspectorControls,
  useBlockProps,
  __experimentalSpacingSizesControl as SpacingSizesControl,
} from "@wordpress/block-editor";
import { justifyLeft, justifyCenter, justifyRight } from "@wordpress/icons";
import { sprintf, __ } from "@wordpress/i18n";
import { useMemo } from "@wordpress/element";
import {
  FormTokenField,
  __experimentalToolsPanel as ToolsPanel,
  __experimentalToolsPanelItem as ToolsPanelItem,
  __experimentalToggleGroupControl as ToggleGroupControl,
  __experimentalToggleGroupControlOptionIcon as ToggleGroupControlOptionIcon,
} from "@wordpress/components";

const DEFAULT_SWATCH_STYLES = {
  borderActiveColor: "#181818",
  borderNormalColor: "#F5F5F5",
  swatchCustomSize: "36",
  swatchDesign: "design_3",
  swatchSize: "medium",
  swatchStyle: "circle",
  imagePosition: "center",
};

const DEFAULT_BUTTON_STYLES = {
  buttonActiveColor: "#181818",
  buttonHoverColor: "#ffffff",
  buttonNormalColor: "#ffffff",
  borderActiveColor: "#181818",
  borderHoverColor: "#181818",
  borderNormalColor: "#d4d4d4",
  borderRadius: "4px",
  borderWidth: "1px",
  buttonSize: "medium",
  textActiveColor: "#ffffff",
  textHoverColor: "#181818",
  textNormalColor: "#181818",
};

const justificationOptions = [
  {
    value: "left",
    icon: justifyLeft,
    label: __("Justify items left"),
  },
  {
    value: "center",
    icon: justifyCenter,
    label: __("Justify items center"),
  },
  {
    value: "right",
    icon: justifyRight,
    label: __("Justify items right"),
  },
];

const ensureObject = (value) =>
  value && typeof value === "object" && !Array.isArray(value) ? value : {};

const getSwatchSize = (styles) => {
  switch (styles.swatchSize) {
    case "small":
      return "28px";
    case "medium":
      return "36px";
    case "large":
      return "44px";
    case "custom":
      return `${
        styles.swatchCustomSize || DEFAULT_SWATCH_STYLES.swatchCustomSize
      }px`;
    default:
      return "28px";
  }
};

const buildSwatchVariablesStyle = (styles) => {
  const merged = { ...DEFAULT_SWATCH_STYLES, ...ensureObject(styles) };
  const radius = merged.swatchStyle === "circle" ? "100%" : "4px";
  const size = getSwatchSize(merged);

  return {
    "--yay-swatches-swatch-border": merged.borderNormalColor,
    "--yay-swatches-swatch-border-active": merged.borderActiveColor,
    "--yay-swatches-swatch-border-radius": radius,
    "--yay-swatches-swatch-size": size,
  };
};

const getButtonSizing = (size) => {
  switch (size) {
    case "small":
      return { fontSize: "12px", padding: "7px 12px" };
    case "large":
      return { fontSize: "16px", padding: "15px 25px" };
    default:
      return { fontSize: "14px", padding: "11px 16px" };
  }
};

const buildButtonVariablesStyle = (styles) => {
  const merged = { ...DEFAULT_BUTTON_STYLES, ...ensureObject(styles) };
  const { fontSize, padding } = getButtonSizing(merged.buttonSize);

  return {
    "--yay-swatches-button": merged.buttonNormalColor,
    "--yay-swatches-button-hover": merged.buttonHoverColor,
    "--yay-swatches-button-active": merged.buttonActiveColor,
    "--yay-swatches-button-border": merged.borderNormalColor,
    "--yay-swatches-button-border-active": merged.borderActiveColor,
    "--yay-swatches-button-border-hover": merged.borderHoverColor,
    "--yay-swatches-button-text": merged.textNormalColor,
    "--yay-swatches-button-text-hover": merged.textHoverColor,
    "--yay-swatches-button-text-active": merged.textActiveColor,
    "--yay-swatches-button-border-radius": merged.borderRadius,
    "--yay-swatches-button-border-width": merged.borderWidth,
    "--yay-swatches-button-font-size": fontSize,
    "--yay-swatches-button-padding": padding,
  };
};

const getSwatchDesignClass = (styles, attributeStyle) => {
  const merged = { ...DEFAULT_SWATCH_STYLES, ...ensureObject(styles) };

  switch (merged.swatchDesign) {
    case "design_1":
      return "yay-swatches-swatch--design-1";
    case "design_2":
      return "yay-swatches-swatch--design-2";
    case "design_3":
      return "yay-swatches-swatch--design-3";
    case "design_4":
      return "yay-swatches-swatch--design-4";
    case "design_5":
      return attributeStyle === "variant_image"
        ? "yay-swatches-swatch-variant-image"
        : "yay-swatches-swatch";
    case "design_6":
      return "yay-swatches-swatch--design-6";
    default:
      return "yay-swatches-swatch--design-3";
  }
};

const getSwatchTypeClass = (attributeStyle) => {
  switch (attributeStyle) {
    case "button":
      return "yay-swatches-button";
    case "variant_image":
      return "yay-swatches-swatch-variant-image";
    default:
      return "yay-swatches-swatch";
  }
};

const getBackgroundPosition = (styles) => {
  switch (styles.imagePosition) {
    case "top":
      return { position: "center top", size: "cover" };
    case "bottom":
      return { position: "center bottom", size: "cover" };
    case "center":
      return { position: "center center", size: "contain" };
    default:
      return { position: "center center", size: "cover" };
  }
};

const buildSwatchColorStyle = (styles, option) => {
  const merged = { ...DEFAULT_SWATCH_STYLES, ...ensureObject(styles) };

  if (option.image) {
    const { position, size } = getBackgroundPosition(merged);
    return {
      backgroundImage: `url(${option.image})`,
      backgroundPosition: position,
      backgroundRepeat: "no-repeat",
      backgroundColor: "transparent",
      backgroundSize: size,
    };
  }

  if (option.showDual && option.dualColor && option.color) {
    const radius = merged.swatchStyle === "circle" ? "50%" : "4px";
    return {
      borderRadius: radius,
      backgroundImage: `linear-gradient(135deg, ${option.color} 50%, ${option.dualColor} 50%)`,
    };
  }

  if (option.color) {
    return {
      backgroundColor: option.color,
    };
  }

  return {};
};

export default function Edit(props) {
  const { attributes, setAttributes } = props;
  const { variants = [], justifyContent = "left" } = attributes;
  const termSpacing = attributes.style?.termSpacing ?? "";

  const localizedVariants = useMemo(() => {
    if (typeof window === "undefined") {
      return [];
    }

    const data = window.brandySwatchesBlock?.variants;

    if (!Array.isArray(data)) {
      return [];
    }

    return data;
  }, []);

  const localizedVariantsOptions = useMemo(() => {
    if (typeof window === "undefined") {
      return [];
    }

    const data = window.brandySwatchesBlock?.variantsOptions;
    if (!Array.isArray(data)) {
      return [];
    }

    return data;
  }, []);

  const globalStyles = useMemo(() => {
    if (typeof window === "undefined") {
      return {
        swatch: DEFAULT_SWATCH_STYLES,
        button: DEFAULT_BUTTON_STYLES,
      };
    }

    const styles = ensureObject(window.brandySwatchesBlock?.styles);

    const swatchStyles = {
      ...DEFAULT_SWATCH_STYLES,
      ...ensureObject(styles.swatch)
    }

    const buttonStyles = {
      ...DEFAULT_BUTTON_STYLES,
      ...ensureObject(styles.button)
    }

    if ( styles.collection?.swatchSize ) {
      swatchStyles.swatchSize = styles.collection.swatchSize;
    }
    if ( styles.collection?.swatchCustomSize ) {
      swatchStyles.swatchCustomSize = styles.collection.swatchCustomSize;
    }
    if ( styles.collection?.buttonSize ) {
      buttonStyles.buttonSize = styles.collection.buttonSize;
    }

    return {
      swatch: swatchStyles,
      button: buttonStyles,
    };
  }, []);

  const suggestionLabels = useMemo(() => {
    return Array.from(
      new Set(localizedVariants.map((variant) => variant.label).filter(Boolean))
    );
  }, [localizedVariants]);

  function getVariantIdByLabel(fieldLabel) {
    const label =
      typeof fieldLabel === "string" ? fieldLabel : fieldLabel.value;

    return Object.values(localizedVariants).find(
      (variant) => variant.label === label
    )?.value;
  }

  const attributeMetaMap = useMemo(() => {
    const defaultMap = new Map();
    defaultMap.set("sample_swatches", {
      label: "Sample swatches",
      value: "sample_swatches",
      style: "custom",
    });
    return localizedVariants.reduce((map, variant) => {
      if (variant?.value) {
        map.set(variant.value, variant);
      }
      return map;
    }, defaultMap);
  }, [localizedVariants]);

  const groupedOptions = useMemo(() => {
    const defaultMap = new Map();
    defaultMap.set("sample_swatches", [
      {
        attribute: 'sample_swatches',
        name: 'Blue',
        slug: 'blue',
        color: '#0000FF'
      },
      {
        attribute: 'sample_swatches',
        name: 'Red',
        slug: 'red',
        color: '#FF0000'
      },
      {
        attribute: 'sample_swatches',
        name: 'Green',
        slug: 'green',
        color: '#008000'
      },
    ])

    return localizedVariantsOptions.reduce((map, option) => {
      if (!option?.attribute) {
        return map;
      }

      if (!map.has(option.attribute)) {
        map.set(option.attribute, []);
      }

      map.get(option.attribute).push(option);
      return map;
    }, defaultMap);
  }, [localizedVariantsOptions]);

  const blockProps = useBlockProps({
    className: `brandy-block-swatches yay-swatches-wrapper is-content-justification-${
      justifyContent ?? "left"
    }`,
    style: termSpacing ? { "--term-spacing": termSpacing } : {},
  });

  const swatchStyles = globalStyles.swatch;
  const buttonStyles = globalStyles.button;

  const groupElements = useMemo(() => {
    const selectedVariants = [...variants];

    if (!variants.length) {
      selectedVariants.push("sample_swatches");
    }

    return selectedVariants
      .map((attributeSlug) => {
        if (!attributeSlug) {
          return null;
        }

        const attributeMeta = attributeMetaMap.get(attributeSlug);
        const options = groupedOptions.get(attributeSlug) || [];

        if (!attributeMeta || !options.length) {
          return null;
        }

        const attributeStyle = attributeMeta.style || "custom";

        if (attributeStyle === "dropdown") {
          return (
            <div
              key={attributeSlug}
              className="brandy-block-swatches__group brandy-block-swatches__group--dropdown"
              data-attribute={attributeSlug}
            >
              <p className="brandy-block-swatches__label yay-swatches-attribute-name">
                {attributeMeta.label || attributeMeta.value}
              </p>
              <select
                className="yay-swatches-dropdown-select"
                data-attribute={attributeMeta.value}
                disabled
              >
                <option value="">
                  {__("Choose an option", "brandy-blocks")}
                </option>
                {options
                  .map((option) => {
                    const optionValue =
                      option?.slug || option?.id || option?.name;

                    if (!optionValue) {
                      return null;
                    }

                    return (
                      <option key={optionValue} value={optionValue}>
                        {option?.name || option?.slug || optionValue}
                      </option>
                    );
                  })
                  .filter(Boolean)}
              </select>
            </div>
          );
        }

        const isButtonStyle = attributeStyle === "button";
        const isRadioStyle = attributeStyle === "radio";
        const wrapperProps = {
          className: "yay-variant-wrapper",
          "data-attribute": attributeMeta.value,
          "data-type": attributeStyle,
          "data-show-tooltip":
            swatchStyles.swatchTooltip === "enable" ? "yes" : "no",
          "data-show-image":
            swatchStyles.swatchTooltipImage === true ||
            swatchStyles.swatchTooltipImage === "enable"
              ? "yes"
              : "no",
          "data-arrow":
            swatchStyles.swatchTooltipArrow === true ||
            swatchStyles.swatchTooltipArrow === "enable"
              ? "yes"
              : "no",
          "data-shadow":
            swatchStyles.swatchTooltipBoxShadow === true ||
            swatchStyles.swatchTooltipBoxShadow === "enable"
              ? "yes"
              : "no",
          "data-animation":
            swatchStyles.swatchTooltipAnimation === true ||
            swatchStyles.swatchTooltipAnimation === "enable"
              ? "yes"
              : "no",
          "data-clear-button":
            swatchStyles.clearButtonShowHideOptions === "hide" &&
            swatchStyles.hideClearButtonOptions !== "shop"
              ? "disable"
              : "enable",
          "data-tick-selected":
            swatchStyles.tickSelected === "enable" ? "enable" : "disable",
        };

        return (
          <div
            key={attributeSlug}
            className="brandy-block-swatches__group"
            data-attribute={attributeSlug}
          >
            <p className="brandy-block-swatches__label yay-swatches-attribute-name">
              {attributeMeta.label || attributeMeta.value}
            </p>
            <div {...wrapperProps}>
              {options.slice(0, 4)
                .map((option) => {
                  const optionKey = `${attributeMeta.value}-${
                    option?.slug || option?.id || option?.name
                  }`;

                  if (!optionKey) {
                    return null;
                  }

                  if (isRadioStyle) {
                    return (
                      <label
                        key={optionKey}
                        className="yay-swatches-radio-wrapper"
                      >
                        <input
                          className="yay-swatches-attribute-term yay-swatches-swatch-radio"
                          data-attribute={attributeMeta.value}
                          data-term={option.slug}
                          data-type="radio"
                          name={`yay-swatches-radio-${attributeMeta.value}`}
                          type="radio"
                          value={option.slug}
                          readOnly
                        />
                        <span className="yay-swatches-radio-label">
                          {option.name || option.slug || option.id}
                        </span>
                      </label>
                    );
                  }

                  if (isButtonStyle) {
                    return (
                      <span
                        key={optionKey}
                        className="yay-swatches-attribute-term yay-swatches-button"
                        data-attribute={attributeMeta.value}
                        data-term={option.slug}
                        data-type={attributeStyle}
                        data-label-text={option.name}
                        data-tippy-text={option.name}
                        style={buildButtonVariablesStyle(buttonStyles)}
                      >
                        {option.name}
                      </span>
                    );
                  }

                  const typeClass = getSwatchTypeClass(attributeStyle);
                  const designClass = getSwatchDesignClass(
                    swatchStyles,
                    attributeStyle
                  );
                  const swatchStyle = buildSwatchVariablesStyle(swatchStyles);
                  const colorStyle = buildSwatchColorStyle(
                    swatchStyles,
                    option
                  );

                  return (
                    <span
                      key={optionKey}
                      className={[
                        "yay-swatches-attribute-term",
                        typeClass,
                        designClass,
                      ]
                        .filter(Boolean)
                        .join(" ")}
                      data-attribute={attributeMeta.value}
                      data-term={option.slug}
                      data-type={attributeStyle}
                      data-label-text={option.name}
                      data-tippy-text={option.name}
                      {...(option.image
                        ? { "data-tooltip-img": option.image }
                        : {})}
                      style={swatchStyle}
                    >
                      <span className="yay-swatches-color" style={colorStyle} />
                    </span>
                  );
                })
                .filter(Boolean)}
            </div>
          </div>
        );
      })
      .filter(Boolean);
  }, [attributeMetaMap, groupedOptions, swatchStyles, buttonStyles, variants]);

  return (
    <>
      <InspectorControls>
        <ToolsPanel label={__("Settings", "brandy-blocks")}>
          <div
            style={{ gridColumn: "1 / -1" }}
            dangerouslySetInnerHTML={{
              __html: window.brandySwatchesBlock?.unSupportedBlock
                ? sprintf(
                    "Please install %s plugin to utilize this block.",
                    `<a href="https://yaycommerce.com/yayswatches-variation-swatches-for-woocommerce/" target="_blank">
              YaySwatches Pro
            </a>`
                  )
                : window.brandySwatchesBlock?.proActive
                ? sprintf(
                    "Customize your %s",
                    `<a href="${window.brandyBlocks.url.site}/wp-admin/admin.php?page=yay-swatches" target="_blank">
                swatches style
              </a>`
                  )
                : sprintf(
                    "Please upgrade %s to fully customize your swatches.",
                    `<a href="https://yaycommerce.com/yayswatches-variation-swatches-for-woocommerce/" target="_blank">
                YaySwatches Pro
              </a>`
                  ),
            }}
          ></div>
          <ToggleGroupControl
            __next40pxDefaultSize
            __nextHasNoMarginBottom
            label={__("Justification")}
            value={justifyContent}
            onChange={(value) => setAttributes({ justifyContent: value })}
            className="block-editor-hooks__flex-layout-justification-controls"
          >
            {justificationOptions.map(({ value, icon, label }) => {
              return (
                <ToggleGroupControlOptionIcon
                  key={value}
                  value={value}
                  icon={icon}
                  label={label}
                />
              );
            })}
          </ToggleGroupControl>
          <ToolsPanelItem
            label={__("Variants", "brandy-blocks")}
            hasValue={() => variants.length > 1}
            onDeselect={() => {
              setAttributes({
                variants: [],
              });
            }}
            isShownByDefault
          >
            <FormTokenField
              className="brandy-swatches-variants"
              label={__("Variants", "brandy-blocks")}
              onChange={(variants) => {
                const updatedVariants = variants
                  .map((variant) => getVariantIdByLabel(variant))
                  .filter(Boolean);
                setAttributes({ variants: updatedVariants });
              }}
              placeholder={__("Default: Available variants", "brandy-blocks")}
              suggestions={suggestionLabels}
              validateInput={(value) => {
                return localizedVariants.some(
                  (variant) => variant.label === value
                );
              }}
              value={
                variants.map(
                  (variant) =>
                    localizedVariants.find((v) => v.value === variant)?.label ??
                    variant
                ) || []
              }
              __experimentalExpandOnFocus={true}
              __experimentalShowHowTo={false}
            />
          </ToolsPanelItem>
        </ToolsPanel>
      </InspectorControls>
      <InspectorControls group="styles">
        <ToolsPanel label={__("Spacing", "brandy-blocks")}>
          <ToolsPanelItem
            label={__("Term spacing", "brandy-blocks")}
            hasValue={() => !!termSpacing}
            onDeselect={() =>
              setAttributes({ style: { ...attributes.style, termSpacing: "" } })
            }
            isShownByDefault
          >
            <SpacingSizesControl
              label={__("Term spacing")}
              min={0}
              onChange={(value) =>
                setAttributes({
                  style: { ...attributes.style, termSpacing: value.top },
                })
              }
              showSideInLabel={false}
              sides={["top"]}
              values={{ top: termSpacing }}
              allowReset={false}
            />
          </ToolsPanelItem>
        </ToolsPanel>
      </InspectorControls>
      <div {...blockProps}>
        {window.brandySwatchesBlock?.unSupportedBlock ? (
          <p>
            {__(
              "Swatches block is not supported, please install YaySwatches plugin.",
              "brandy-blocks"
            )}
          </p>
        ) : groupElements.length > 0 ? (
          groupElements
        ) : (
          <p>{__("No swatches available.", "brandy-blocks")}</p>
        )}
      </div>
    </>
  );
}
